<template>
  <div class="color-picker-dropdown" ref="dropdown">
    <button
      class="color-picker-button"
      @click="toggleDropdown"
      :title="title"
      type="button"
    >
      <span class="bbl-color-icon" :style="{ color: currentColor || '#000000' }">A</span>
      <span class="bbl-color-bar" :style="{ backgroundColor: currentColor || '#000000' }"></span>
      <span class="dropdown-arrow">▼</span>
    </button>
    
    <div v-if="isOpen" class="color-dropdown-panel">
      <div class="color-section">
        <button
          class="color-action-button"
          @click="removeColor"
          type="button"
        >
          <span class="remove-icon">✕</span> Remove Color
        </button>
      </div>
      
      <div class="color-section">
        <div class="section-label">Theme Colors</div>
        <div class="color-grid">
          <button
            v-for="color in themeColors"
            :key="color.value"
            class="color-swatch"
            :style="{ backgroundColor: color.value }"
            :title="color.name"
            @click="selectColor(color.value)"
            type="button"
          >
            <span v-if="currentColor === color.value" class="check-mark">✓</span>
          </button>
        </div>
      </div>
      
      <div class="color-section">
        <div class="section-label">Standard Colors</div>
        <div class="color-grid">
          <button
            v-for="color in standardColors"
            :key="color.value"
            class="color-swatch"
            :style="{ backgroundColor: color.value }"
            :title="color.name"
            @click="selectColor(color.value)"
            type="button"
          >
            <span v-if="currentColor === color.value" class="check-mark">✓</span>
          </button>
        </div>
      </div>
      
      <div class="color-section">
        <div class="section-label">Custom Color</div>
        <div class="custom-color-picker">
          <input
            type="color"
            v-model="customColor"
            @change="selectColor(customColor)"
            class="color-input"
          />
          <input
            type="text"
            v-model="customColor"
            @keyup.enter="selectColor(customColor)"
            placeholder="#000000"
            class="color-text-input"
            maxlength="7"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorPickerDropdown',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: 'Text Color'
    }
  },
  data() {
    return {
      isOpen: false,
      customColor: '#000000',
      themeColors: [
        { name: 'Black', value: '#000000' },
        { name: 'Dark Gray', value: '#434343' },
        { name: 'Gray', value: '#666666' },
        { name: 'Light Gray', value: '#999999' },
        { name: 'White', value: '#FFFFFF' },
        { name: 'Red', value: '#E74C3C' },
        { name: 'Orange', value: '#E67E22' },
        { name: 'Yellow', value: '#F1C40F' },
        { name: 'Green', value: '#2ECC71' },
        { name: 'Blue', value: '#3498DB' },
        { name: 'Purple', value: '#9B59B6' },
        { name: 'Pink', value: '#E91E63' }
      ],
      standardColors: [
        { name: 'Dark Red', value: '#C0392B' },
        { name: 'Red', value: '#E74C3C' },
        { name: 'Light Red', value: '#EC7063' },
        { name: 'Dark Orange', value: '#D35400' },
        { name: 'Orange', value: '#E67E22' },
        { name: 'Light Orange', value: '#EB984E' },
        { name: 'Dark Yellow', value: '#F39C12' },
        { name: 'Yellow', value: '#F1C40F' },
        { name: 'Light Yellow', value: '#F4D03F' },
        { name: 'Dark Green', value: '#27AE60' },
        { name: 'Green', value: '#2ECC71' },
        { name: 'Light Green', value: '#58D68D' },
        { name: 'Dark Teal', value: '#16A085' },
        { name: 'Teal', value: '#1ABC9C' },
        { name: 'Light Teal', value: '#48C9B0' },
        { name: 'Dark Blue', value: '#2980B9' },
        { name: 'Blue', value: '#3498DB' },
        { name: 'Light Blue', value: '#5DADE2' },
        { name: 'Dark Purple', value: '#8E44AD' },
        { name: 'Purple', value: '#9B59B6' },
        { name: 'Light Purple', value: '#AF7AC5' },
        { name: 'Dark Pink', value: '#AD1457' },
        { name: 'Pink', value: '#E91E63' },
        { name: 'Light Pink', value: '#F06292' }
      ]
    }
  },
  computed: {
    currentColor() {
      return this.modelValue
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },
    selectColor(color) {
      this.$emit('change', color)
      this.isOpen = false
    },
    removeColor() {
      this.$emit('remove')
      this.isOpen = false
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.color-picker-dropdown {
  position: relative;
  display: inline-block;
}

.color-picker-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.color-picker-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.bbl-color-icon {
  font-weight: bold;
  font-size: 20px;
}

.bbl-color-bar {
  width: 20px;
  height: 3px;
  border-radius: 1px;
}

.dropdown-arrow {
  font-size: 8px;
  color: #666;
}

.color-dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  min-width: 220px;
}

.color-section {
  margin-bottom: 12px;
}

.color-section:last-child {
  margin-bottom: 0;
}

.section-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.color-action-button {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.color-action-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.remove-icon {
  color: #e74c3c;
  font-weight: bold;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.color-swatch {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
}

.color-swatch:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.check-mark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
  font-size: 14px;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.custom-color-picker {
  display: flex;
  gap: 6px;
  align-items: center;
}

.color-input {
  width: 40px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.color-text-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
}

.color-text-input:focus {
  outline: none;
  border-color: #3498db;
}
</style>
